import { useState, useEffect } from 'react';
import { useSettings, DeliveryLocation } from '@/contexts/SettingsContext';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { cn } from '@/lib/utils';

interface DeliveryLocationSelectProps {
  currency: string;
  onSelectLocation: (location: DeliveryLocation | null) => void;
  className?: string;
  locations?: DeliveryLocation[];
}

export function DeliveryLocationSelect({ 
  currency,
  onSelectLocation, 
  className,
  locations: propLocations
}: DeliveryLocationSelectProps) {
  const { checkoutOptions } = useSettings();
  const [selectedLocationId, setSelectedLocationId] = useState<string>('');
  
  const locations = propLocations || checkoutOptions.productDelivery?.deliveryLocations || [];
  const enableDelivery = checkoutOptions.productDelivery?.enableDelivery ?? true;
  const enablePickup = checkoutOptions.productDelivery?.enablePickup ?? true;

  const formatPrice = (price: number) => {
    return price.toLocaleString("en-NG", {
      style: "currency",
      currency: currency || "NGN",
    });
  };
  
  useEffect(() => {
    // If there are locations and one is selected, notify parent
    if (selectedLocationId) {
      const location = locations.find((loc, index) => `${index}` === selectedLocationId);
      if (location) {
        onSelectLocation(location);
      }
    } else {
      onSelectLocation(null);
    }
  }, [selectedLocationId, locations, onSelectLocation]);
  
  // If delivery is not enabled, don't render the component
  if (!enableDelivery || locations.length === 0) {
    return null;
  }
  
  return (
    <div className={cn("space-y-1", className)}>
      <RadioGroup 
        value={selectedLocationId} 
        onValueChange={setSelectedLocationId}
        className="space-y-1"
      >
        {locations.map((location, index) => (
          <div 
            key={index} 
            className="flex items-center justify-between rounded-md border p-3 cursor-pointer hover:bg-gray-50 bg-white"
          >
            <div className="flex items-center space-x-3">
              <RadioGroupItem value={`${index}`} id={`location-${index}`} />
              <Label 
                htmlFor={`location-${index}`} 
                className="flex-1 cursor-pointer font-normal"
              >
                {location.name}
              </Label>
            </div>
            <div className="font-medium">
             {formatPrice(location.price)}
            </div>
          </div>
        ))}
      </RadioGroup>
    </div>
  );
} 